import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Typography,
    CircularProgress,
    Box,
    TextField,
    Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Cookies from "universal-cookie";
import mizanListAPI from "../../Network/MizanList";
import Swal from "sweetalert2";

const cookies = new Cookies();

export default function BelongingsDialog({ open, setOpen, mizan }) {
    const [t] = useTranslation("common");
    const maintheme = useSelector((state) => state.themeData.maintheme);
    const [loading, setLoading] = useState(false);
    const [belongings, setBelongings] = useState([]);
    const [newBelongingName, setNewBelongingName] = useState("");
    const [addingNew, setAddingNew] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const [savingBelonging, setSavingBelonging] = useState(false);

    const getAuthParams = () => ({
        username: cookies.get('user_name'),
        user_id: cookies.get('user_id'),
        account_name: cookies.get('account_name'),
        account_id: cookies.get('account_id'),
        sid: cookies.get('sid'),
        baseurl: cookies.get('baseUrl'),
        id: cookies.get('id'),
    });

    useEffect(() => {
        if (open && mizan) {
            loadBelongings();
        }
    }, [open, mizan]);

    const loadBelongings = async () => {
        setLoading(true);
        try {
            const result = await mizanListAPI.getBelongings({
                id: mizan.id,
                params: getAuthParams()
            });

            if (result?.status && result?.data?.data) {
                setBelongings(result.data.data);
            }
        } catch (error) {
            console.log("Error loading belongings:", error);
        }
        setLoading(false);
    };

    const handleClose = () => {
        setOpen(false);
        setAddingNew(false);
        setNewBelongingName("");
        setEditingId(null);
        setEditingName("");
    };

    const handleAddBelonging = async () => {
        if (!newBelongingName.trim()) {
            toast.warn(t('MIZAN_LIST.belonging_name') + " " + t('GLOBAL.mandatory_field'));
            return;
        }

        setSavingBelonging(true);
        try {
            const result = await mizanListAPI.addBelonging({
                mizanId: mizan.id,
                params: getAuthParams(),
                data: { mizan_belonging_name: newBelongingName }
            });

            if (result?.status && result?.data?.status) {
                toast.success(t('NETWORK_MESSAGES.insertSuccess') || 'تمت الإضافة بنجاح');
                setNewBelongingName("");
                setAddingNew(false);
                loadBelongings();
            } else {
                toast.error(result?.data?.data?.message || t('NETWORK_MESSAGES.unknownError'));
            }
        } catch (error) {
            toast.error(t('NETWORK_MESSAGES.unknownError'));
        }
        setSavingBelonging(false);
    };

    const handleUpdateBelonging = async () => {
        if (!editingName.trim()) {
            toast.warn(t('MIZAN_LIST.belonging_name') + " " + t('GLOBAL.mandatory_field'));
            return;
        }

        setSavingBelonging(true);
        try {
            const result = await mizanListAPI.updateBelonging({
                mizanId: mizan.id,
                belongingId: editingId,
                params: getAuthParams(),
                data: { mizan_belonging_name: editingName }
            });

            if (result?.status && result?.data?.status) {
                toast.success(t('NETWORK_MESSAGES.updateSuccess') || 'تم التحديث بنجاح');
                setEditingId(null);
                setEditingName("");
                loadBelongings();
            } else {
                toast.error(result?.data?.data?.message?.text || t('NETWORK_MESSAGES.unknownError'));
            }
        } catch (error) {
            toast.error(t('NETWORK_MESSAGES.unknownError'));
        }
        setSavingBelonging(false);
    };

    const handleDeleteBelonging = async (belonging) => {
        Swal.fire({
            title: t('GLOBAL.are_you_sure') || 'هل أنت متأكد؟',
            text: t('GLOBAL.delete_confirm') || 'لن تتمكن من التراجع عن هذا!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('GLOBAL.yes_delete') || 'نعم، احذفه!',
            cancelButtonText: t('GLOBAL.action_cancel') || 'إلغاء',
            backdrop: `rgba(0,0,0,0.8)`,
            customClass: {
                container: 'swal-high-z-index'
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await mizanListAPI.deleteBelonging({
                        mizanId: mizan.id,
                        belongingId: belonging.id,
                        params: getAuthParams()
                    });
                    if (res.status && res?.data?.status) {
                        toast.success(t('NETWORK_MESSAGES.deleteSuccess') || 'تم الحذف بنجاح');
                        loadBelongings();
                    } else {
                        toast.error(res?.data?.data?.message?.text || t('NETWORK_MESSAGES.unknownError'));
                    }
                } catch (error) {
                    toast.error(t('NETWORK_MESSAGES.unknownError'));
                }
            }
        });
    };

    const startEdit = (belonging) => {
        setEditingId(belonging.id);
        setEditingName(belonging.mizan_belonging_name);
        setAddingNew(false);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingName("");
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: "12px", minHeight: "400px" }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: maintheme?.primary,
                    color: "#fff",
                    fontFamily: "Cairo-Bold",
                }}
            >
                {t("MIZAN_LIST.belongings_title") || "الانتماءات"} - {mizan?.display_name}
                <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "16px" }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Add New Belonging Section */}
                        <Box sx={{ mb: 2, mt: 1 }}>
                            {addingNew ? (
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={newBelongingName}
                                        onChange={(e) => setNewBelongingName(e.target.value)}
                                        placeholder={t("MIZAN_LIST.belonging_name") || "اسم الانتماء"}
                                        InputProps={{ sx: { fontFamily: "Cairo-Medium" } }}
                                        autoFocus
                                    />
                                    <IconButton
                                        onClick={handleAddBelonging}
                                        disabled={savingBelonging}
                                        sx={{ color: maintheme?.primary }}
                                    >
                                        {savingBelonging ? <CircularProgress size={20} /> : <SaveIcon />}
                                    </IconButton>
                                    <IconButton onClick={() => { setAddingNew(false); setNewBelongingName(""); }}>
                                        <CancelIcon />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => { setAddingNew(true); setEditingId(null); }}
                                    sx={{ fontFamily: "Cairo-Medium" }}
                                >
                                    {t("MIZAN_LIST.add_belonging") || "إضافة انتماء"}
                                </Button>
                            )}
                        </Box>
                        <Divider sx={{ mb: 1 }} />

                        {/* Belongings List */}
                        <List>
                            {belongings.length === 0 ? (
                                <Typography sx={{ textAlign: "center", fontFamily: "Cairo-Medium", color: "grey", mt: 4 }}>
                                    {t("GLOBAL.no_data") || "لا توجد بيانات"}
                                </Typography>
                            ) : (
                                belongings.map((belonging) => (
                                    <ListItem key={belonging.id} divider>
                                        {editingId === belonging.id ? (
                                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%" }}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={editingName}
                                                    onChange={(e) => setEditingName(e.target.value)}
                                                    InputProps={{ sx: { fontFamily: "Cairo-Medium" } }}
                                                    autoFocus
                                                />
                                                <IconButton
                                                    onClick={handleUpdateBelonging}
                                                    disabled={savingBelonging}
                                                    sx={{ color: maintheme?.primary }}
                                                >
                                                    {savingBelonging ? <CircularProgress size={20} /> : <SaveIcon />}
                                                </IconButton>
                                                <IconButton onClick={cancelEdit}>
                                                    <CancelIcon />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <>
                                                <ListItemText
                                                    primary={belonging.mizan_belonging_name}
                                                    primaryTypographyProps={{ fontFamily: "Cairo-Medium" }}
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => startEdit(belonging)}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <EditIcon sx={{ color: maintheme?.primary }} />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleDeleteBelonging(belonging)}
                                                    >
                                                        <DeleteIcon sx={{ color: "#d32f2f" }} />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </>
                                        )}
                                    </ListItem>
                                ))
                            )}
                        </List>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: "16px" }}>
                <Button
                    onClick={handleClose}
                    sx={{ fontFamily: "Cairo-Medium" }}
                >
                    {t("GLOBAL.action_close") || "إغلاق"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
